/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment, PureComponent } from 'react'
import { defineMessages, injectIntl } from 'react-intl'

import {
  EuiCallOut,
  EuiLoadingSpinner,
  EuiButtonIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiToolTip,
} from '@elastic/eui'
import { executeScript } from '@elastic/micro-jq'

import { FormattedMessage } from '@modules/i18n'
import type {
  AjaxRequestErrorType,
  AsyncRequestState,
  EsProxyResponseConsole,
} from '@modules/ui-types'
import { CuiCodeBlock } from '@modules/cui/CodeBlock'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'
import CopyButton from '@modules/cui/CopyButton'

import type { ConsoleRequestState } from '../../reducers/apiConsole'
import type { WrappedComponentProps } from 'react-intl'
import type { ReactNode } from 'react'

type Props = WrappedComponentProps & {
  requestSettings: ConsoleRequestState
  request: AsyncRequestState
  response?: EsProxyResponseConsole | null
}

const messages = defineMessages({
  rawOutput: {
    id: `api-console-output.raw-output`,
    defaultMessage: `Raw output`,
  },
})

class APIConsoleOutput extends PureComponent<Props> {
  render() {
    const { request, response } = this.props

    if (request.inProgress) {
      return <EuiLoadingSpinner />
    }

    if (!request.error && !response) {
      return null
    }

    return (
      <section>
        {this.renderError()}
        {this.renderResponse()}
      </section>
    )
  }

  renderError() {
    const { request } = this.props

    if (!request.error) {
      return null
    }

    const {
      body,
      response: { status, statusText },
    } = request.error as AjaxRequestErrorType

    const responseText = indent(body)

    let error: ReactNode

    if (status === 449) {
      error = (
        <EuiCallOut
          data-test-id='api-console-output.request-permissions-error'
          iconType='alert'
          color='danger'
          title={
            <FormattedMessage
              id='api-console-output.permissions-error.title'
              defaultMessage='Additional permissions needed to perform this operation'
            />
          }
        >
          <FormattedMessage
            id='api-console-output.permissions-error.body'
            defaultMessage='Contact your administrator about your role.'
          />
        </EuiCallOut>
      )
    } else {
      error = (
        <EuiCallOut
          data-test-id='api-console-output.request-error'
          color={classifyStatus(status)}
          title={
            <FormattedMessage
              id='api-console-output.request-status'
              defaultMessage='{ status } — { statusText }'
              values={{
                status,
                statusText,
              }}
            />
          }
        />
      )
    }

    return (
      <div>
        {error}

        <CuiCodeBlock
          overflowHeight={500}
          language='json'
          css={css`
            word-break: break-word;
          `}
        >
          {responseText}
        </CuiCodeBlock>
      </div>
    )
  }

  renderResponse() {
    const {
      request,
      response,
      requestSettings,
      intl: { formatMessage },
    } = this.props

    if (!response || request.error) {
      return null
    }

    const { body, contentType = ``, status, statusText, requestDuration } = response

    const isJson = contentType.includes(`json`)
    const language = isJson ? `json` : `text`
    let responseText = isJson ? indent(body) : body

    if (requestSettings.advancedMode) {
      switch (requestSettings.filterBy) {
        case 'jq':
          if (!isJson || !requestSettings.filterJq) {
            break
          }

          try {
            const filtered = executeScript(body, requestSettings.filterJq)
            responseText = indent(filtered)
          } catch (e) {
            // invalid expression, leave response alone
          }

          break

        case 'regex':
          if (!requestSettings.filterRegex) {
            break
          }

          try {
            const regex = new RegExp(requestSettings.filterRegex)
            const lineFilter = requestSettings.invertFilter
              ? (line) => !regex.test(line)
              : (line) => regex.test(line)
            responseText = responseText.split(/\n/).filter(lineFilter).join('\n')
          } catch (e) {
            // invalid regex, leave response alone
          }

          break

        default:
          console.error(`Unknown filter mode: ${requestSettings.filterBy}`)
          break
      }
    }

    return (
      <Fragment>
        <EuiCallOut
          data-test-id='api-console-output.response-status'
          color={classifyStatus(status)}
          title={
            <FormattedMessage
              id='api-console-output.response-status'
              defaultMessage='{ status } — { statusText } { duration }'
              values={{
                status,
                statusText,
                duration: requestDuration ? `(${requestDuration} ms)` : ``,
              }}
            />
          }
        />
        <PrivacySensitiveContainer>
          <CuiCodeBlock
            isVirtualized={true}
            overflowHeight={500}
            language={language}
            css={css`
              word-break: break-word;
            `}
          >
            {responseText}
          </CuiCodeBlock>
        </PrivacySensitiveContainer>

        <EuiSpacer size='xs' />
        <EuiFlexGroup justifyContent='flexEnd' gutterSize='s'>
          <EuiFlexItem grow={false}>
            <EuiToolTip
              position='top'
              content={formatMessage(messages.rawOutput)}
              disableScreenReaderOutput={true}
            >
              <EuiButtonIcon
                iconType='popout'
                href={this.getBlob(responseText)}
                aria-label={formatMessage(messages.rawOutput)}
                target='_blank'
              />
            </EuiToolTip>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <CopyButton value={responseText} />
          </EuiFlexItem>
        </EuiFlexGroup>
      </Fragment>
    )
  }

  getBlob = (responseText: string) => {
    const blob: Blob = new window.Blob([responseText], {
      type: `application/json;charset=utf-8`,
    })

    const blobUrl: string = window.URL.createObjectURL(blob)

    return blobUrl
  }
}

function indent(json) {
  try {
    return JSON.stringify(json, null, 2)
  } catch (err) {
    return json // failed to indent, just use raw output
  }
}

function classifyStatus(status) {
  switch (Math.floor(status / 100)) {
    case 1:
      return `success`
    case 2:
      return `success`
    case 3:
      return `primary`
    default:
      return `danger`
  }
}

export default injectIntl(APIConsoleOutput)
